import { addDays, endOfMonth, format, startOfMonth, subDays } from "date-fns";

// The wire format, and the only shape the date filters hand back. A bare calendar day
// carries no time and no offset, so the server is free to resolve both ends in the
// application timezone — which is where "today" has to mean today, not 07:00 yesterday.
export const DATE_PARAM_FORMAT = "yyyy-MM-dd";

export type DateRangeValue = { from: string; to: string };

export const EMPTY_DATE_RANGE: DateRangeValue = { from: "", to: "" };

// Parsed at local midnight on purpose: these Dates only ever feed the calendar and the
// trigger label, both of which think in calendar days. Anchoring at midnight means no
// arithmetic here can cross a day boundary and shift the day the admin picked.
export const parseDayInput = (value: string): Date | undefined => {
	if (!value) return undefined;

	const parsed = new Date(`${value}T00:00:00`);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const formatDayInput = (value: Date | undefined): string =>
	value ? format(value, DATE_PARAM_FORMAT) : "";

export type PresetKey =
	| "today"
	| "last_7_days"
	| "last_30_days"
	| "next_7_days"
	| "next_30_days"
	| "this_month";

// Every preset is a calendar-day pair, so choosing one and drawing the same range on the
// calendar produce identical params — presets are a shortcut into the range, not a second
// filter beside it.
//
// The forward pair mirrors the backward one exactly: both count today as one of their N
// days, so "last 7" and "next 7" span the same length in opposite directions and neither
// is off by one against the other.
export const presetRange = (key: PresetKey, today: Date): DateRangeValue => {
	switch (key) {
		case "today":
			return { from: formatDayInput(today), to: formatDayInput(today) };
		case "last_7_days":
			// Inclusive of today, so seven days total rather than eight.
			return {
				from: formatDayInput(subDays(today, 6)),
				to: formatDayInput(today),
			};
		case "last_30_days":
			return {
				from: formatDayInput(subDays(today, 29)),
				to: formatDayInput(today),
			};
		case "next_7_days":
			return {
				from: formatDayInput(today),
				to: formatDayInput(addDays(today, 6)),
			};
		case "next_30_days":
			return {
				from: formatDayInput(today),
				to: formatDayInput(addDays(today, 29)),
			};
		case "this_month":
			return {
				from: formatDayInput(startOfMonth(today)),
				to: formatDayInput(endOfMonth(today)),
			};
		default: {
			// Every key is spelled out above, so this branch is unreachable and `key` has
			// narrowed to never. A seventh preset added without a case here stops compiling
			// instead of quietly filtering on today.
			const unhandled: never = key;
			throw new Error(`Unhandled date range preset: ${unhandled}`);
		}
	}
};

// Which shortcuts a filter offers depends on which way it points in time: a draft cannot
// be created in the future, while an appointment is normally scheduled into it. The two
// call sites therefore pass different lists rather than share one module-wide default.
export const BACKWARD_PRESETS: PresetKey[] = [
	"today",
	"last_7_days",
	"last_30_days",
	"this_month",
];

export const BIDIRECTIONAL_PRESETS: PresetKey[] = [
	"today",
	"last_7_days",
	"last_30_days",
	"next_7_days",
	"next_30_days",
	"this_month",
];

// Two presets can describe the very same pair of days, and then only one of them can be
// shown as active. On the last day of a 30-day month "last 30 days" spans exactly the
// month, and on the 1st of one "next 30 days" does; February can never collide, because
// no 28- or 29-day span reaches back or forward a full 30.
//
// The tie goes to the more specific intent: somebody who clicks "This month" means the
// month, not the thirty days that happen to coincide with it that once a quarter. Both
// directions are ranked here rather than only the forward one, so the two behave alike.
//
// Deliberately a separate order from the rendered list. The buttons stay where the filter
// puts them and only the *matching* is ranked, so fixing which one lights up never moves
// anything on screen.
//
// A rank per key rather than an ordered list, because a list can silently omit one: a
// preset missing from it would never light up, which is the very bug this table exists to
// fix. As a Record the compiler refuses a new key that nobody ranked.
export const MATCH_PRECEDENCE: Record<PresetKey, number> = {
	today: 0,
	this_month: 1,
	last_7_days: 2,
	next_7_days: 3,
	last_30_days: 4,
	next_30_days: 5,
};

// Which of the offered presets, if any, this range already is. Only what the caller offers
// is considered, so a filter is never told it selected a shortcut it does not show, and
// that list is scanned in MATCH_PRECEDENCE order rather than render order.
export const resolveActivePreset = (
	value: DateRangeValue,
	today: Date,
	presets: readonly PresetKey[],
): PresetKey | undefined =>
	[...presets]
		.sort((a, b) => MATCH_PRECEDENCE[a] - MATCH_PRECEDENCE[b])
		.find((key) => {
			const range = presetRange(key, today);
			return range.from === value.from && range.to === value.to;
		});
